import { type PropsWithChildren, type ReactNode } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { fasUser } from 'shared/@generated/icons/fasUser';
import { Icon } from 'shared/components/icon';
import { Text } from 'shared/components/text';
import { ImageTile, TextLine } from './illustration-interface';
import { illustrationPalette as palette, illustrationImagePalettes } from './illustration-palette';
import { IllustrationScene, type IllustrationScale } from './illustration-scene';

export type IllustrationProps = { transparent?: boolean };
export type PhotoTone = keyof typeof illustrationImagePalettes;

export function useIllustrationCopy() {
  const { t } = useTranslation('components');
  return (key: string) => t(`illustration_samples.${key}`);
}

export function IllustrationFrame({
  transparent = false,
  children,
}: IllustrationProps & { children: (s: IllustrationScale) => ReactNode }) {
  return (
    <View
      className="aspect-square w-full overflow-hidden rounded-2xl"
      style={{ backgroundColor: transparent ? undefined : palette.sageLight }}
    >
      <IllustrationScene transparent>{children}</IllustrationScene>
    </View>
  );
}

export function Phone({
  s,
  x,
  y,
  width = 58,
  angle = 0,
  children,
}: PropsWithChildren<{
  s: IllustrationScale;
  x: number;
  y: number;
  width?: number;
  angle?: number;
}>) {
  return (
    <View
      testID="illustration-phone"
      className="absolute"
      style={{
        left: s(x),
        top: s(y),
        width: s(width),
        height: s((width * 124) / 58),
        transform: [{ rotate: `${angle}deg` }],
      }}
    >
      <View
        className="absolute rounded-full"
        style={{
          backgroundColor: palette.ink,
          left: s(-0.7),
          top: s(24),
          width: s(1.2),
          height: s(8),
        }}
      />
      <View
        className="absolute rounded-full"
        style={{
          backgroundColor: palette.ink,
          right: s(-0.7),
          top: s(30),
          width: s(1.2),
          height: s(11),
        }}
      />
      <View
        className="absolute inset-0 overflow-hidden"
        style={{
          backgroundColor: palette.paper,
          borderColor: palette.ink,
          borderWidth: s(1.6),
          borderRadius: s(12),
        }}
      >
        <View className="items-center" style={{ height: s(10), paddingTop: s(2.4) }}>
          <View
            className="rounded-full"
            style={{ backgroundColor: palette.ink, width: s(19), height: s(3.4) }}
          />
        </View>
        <View
          className="flex-1 overflow-hidden"
          style={{ paddingHorizontal: s(4), paddingBottom: s(8) }}
        >
          {children}
        </View>
        <View
          className="absolute self-center rounded-full"
          style={{
            bottom: s(3.2),
            width: s(19),
            height: s(1.5),
            backgroundColor: palette.ink,
            opacity: 0.35,
          }}
        />
      </View>
    </View>
  );
}

export function CroppedPhone({
  s,
  width,
  y = 10,
  children,
}: {
  s: IllustrationScale;
  width: number;
  y?: number;
  children: ReactNode;
}) {
  const factor = width / 58;
  return (
    <Phone s={(value) => s(value * factor)} x={(160 - width) / 2 / factor} y={y / factor}>
      {children}
    </Phone>
  );
}

export function Lines({
  s,
  width,
  rows = 3,
}: {
  s: IllustrationScale;
  width: number;
  rows?: number;
}) {
  return (
    <View style={{ gap: s(2.5) }}>
      {Array.from({ length: rows }, (_, i) => (
        <TextLine
          key={i}
          s={s}
          width={width * (i === rows - 1 ? 0.7 : 1)}
          height={1.5}
          className={i === 0 ? 'bg-brand-brown-dark/30' : 'bg-brand-brown-dark/15'}
        />
      ))}
    </View>
  );
}

export function Photo({
  s,
  width,
  height,
  tone = 'forest',
}: {
  s: IllustrationScale;
  width: number;
  height: number;
  tone?: PhotoTone;
}) {
  return (
    <ImageTile
      s={s}
      width={width}
      height={height}
      motif="mountain"
      colors={illustrationImagePalettes[tone]}
    />
  );
}

export function Caption({
  s,
  children,
  size = 6,
  muted = false,
  green = false,
  center = false,
}: PropsWithChildren<{
  s: IllustrationScale;
  size?: number;
  muted?: boolean;
  green?: boolean;
  center?: boolean;
}>) {
  return (
    <Text
      className={muted ? 'font-normal' : 'font-semibold'}
      numberOfLines={1}
      style={{
        fontSize: s(size),
        lineHeight: s(size * 1.45),
        color: green ? palette.green : palette.ink,
        opacity: muted ? 0.6 : 1,
        textAlign: center ? 'center' : 'left',
      }}
    >
      {children}
    </Text>
  );
}

export function Avatar({ s, size = 13 }: { s: IllustrationScale; size?: number }) {
  return (
    <View
      className="items-center justify-center rounded-full"
      style={{
        width: s(size),
        height: s(size),
        backgroundColor: palette.sageLight,
        borderWidth: s(0.7),
        borderColor: palette.paper,
      }}
    >
      <Icon
        icon={fasUser}
        className="text-brand-green-dark"
        style={{ width: s(size * 0.43), height: s(size * 0.43) }}
      />
    </View>
  );
}
